import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AppDatePicker from "./common/AppDatePicker";
import { useFile } from "../context/FileContext";
import { useAuth } from "../context/AuthContext";
import { bidDraftKey, forgetBidDraft, lastBidDraftScope, readBidDraft, rememberBidDraft } from "../services/marketBidDrafts";
import { loadMarketBidBook, saveMarketBidBook, serializeBidBook } from "../services/marketBidBooks";

const LABELS = {
  min_acceptance_ratio: "Minimum acceptance ratio (0–1)", parent_id: "Parent block",
  exclusive_group: "Exclusive group", duration_periods: "Duration (15-minute periods)",
  display_quantity_mw: "Visible quantity (MW)", min_quantity_mw: "Minimum fill (MW)",
  time_in_force: "Execution condition", expires_at: "Expiry (ISO timestamp with UTC offset)",
  link_group: "Linked basket group", product: "Reserve product",
};
const NUMERIC = new Set(["min_acceptance_ratio", "duration_periods", "display_quantity_mw", "min_quantity_mw"]);
const ARRAY_FIELDS = new Set(["curve_steps", "profile_mw"]);
const COMMON = new Set(["id", "bid_type", "side", "period_start", "period_end", "quantity_mw", "price"]);
const makeId = () => `bid-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
const idOf = (item) => typeof item === "string" ? item : item.id;
const labelOf = (item) => typeof item === "string" ? item : item.label || item.id;

export function orderForSave(draft, type) {
  const result = {
    id: draft.id, bid_type: draft.bid_type, side: draft.side,
    period_start: Number(draft.period_start), period_end: Number(draft.period_end),
    quantity_mw: draft.quantity_mw === "" ? undefined : Number(draft.quantity_mw),
    price: draft.price === "" ? undefined : Number(draft.price),
  };
  (type?.fields || []).forEach((field) => {
    if (COMMON.has(field) || draft[field] === undefined || draft[field] === "") return;
    if (field === "curve_steps") result[field] = draft[field].map((step) => ({ quantity_mw: Number(step.quantity_mw), price: Number(step.price) }));
    else if (field === "profile_mw") result[field] = String(draft[field]).split(/[\s,;]+/).filter(Boolean).map(Number);
    else result[field] = NUMERIC.has(field) ? Number(draft[field]) : draft[field];
  });
  return result;
}

function OrderEditor({ open, order, book, orders, onClose, onSave }) {
  const [draft, setDraft] = useState({});
  const [error, setError] = useState("");
  const types = book.market?.bid_types || [];
  const type = types.find((item) => item.id === draft.bid_type);
  const fields = type?.fields || [];
  const required = new Set(type?.required_fields || []);
  const periods = book.periods || [];
  useEffect(() => {
    if (!open) return;
    setDraft(order ? {
      ...order,
      profile_mw: Array.isArray(order.profile_mw) ? order.profile_mw.join(", ") : order.profile_mw,
    } : { id: makeId(), bid_type: "", side: "", period_start: "", period_end: "", quantity_mw: "", price: "" });
    setError("");
  }, [open, order]);
  const update = (field, value) => setDraft((previous) => ({
    ...previous, [field]: value,
    ...(field === "time_in_force" && value !== "NON" ? { expires_at: undefined } : {}),
  }));
  const chooseType = (value) => setDraft((previous) => ({
    id: previous.id, bid_type: value, side: previous.side,
    period_start: previous.period_start, period_end: previous.period_end,
    quantity_mw: previous.quantity_mw, price: previous.price,
  }));
  const finish = () => {
    if (!type || !draft.side || !draft.period_start || !draft.period_end) {
      setError("Choose a bid type, direction and delivery periods."); return;
    }
    if (Number(draft.period_end) < Number(draft.period_start)) {
      setError("The last period must follow the first period."); return;
    }
    if (draft.quantity_mw === "" || draft.price === "" || !(Number(draft.quantity_mw) > 0) || !Number.isFinite(Number(draft.price))) {
      setError("Enter a positive quantity and a finite price. Negative energy prices are allowed."); return;
    }
    for (const field of required) {
      if (draft[field] === undefined || draft[field] === "" || (Array.isArray(draft[field]) && !draft[field].length)) {
        setError(`Enter ${LABELS[field] || field.replaceAll("_", " ")}.`); return;
      }
    }
    if (fields.includes("curve_steps") && draft.curve_steps?.some((step) => step.quantity_mw === "" || step.price === "" || !(Number(step.quantity_mw) > 0) || !Number.isFinite(Number(step.price)))) {
      setError("Each price step needs a positive quantity and a finite price."); return;
    }
    const clean = orderForSave(draft, type);
    if (clean.profile_mw?.some((value) => !Number.isFinite(value) || value < 0)) {
      setError("Enter non-negative profile quantities separated by commas."); return;
    }
    onSave(clean);
  };
  const directions = book.market?.directions || ["buy", "sell"];
  const conditions = type?.time_in_force || type?.time_in_force_options || (
    draft.bid_type === "continuous_block" ? ["AON"] : draft.bid_type === "linked_basket" ? ["FOK"] :
      draft.bid_type === "iceberg" ? ["NON"] : ["NON", "IOC", "FOK"]
  );
  const periodLabel = (period) => `${period.index} · ${period.label || period.start}`;
  return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>{order ? "Edit order" : "Add order"}</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField select label="Bid type" value={draft.bid_type || ""} onChange={(event) => chooseType(event.target.value)} fullWidth>
          {types.map((item) => <MenuItem key={item.id} value={item.id} title={item.disabled_reason || item.warning} disabled={item.available === false || item.enabled === false}>{item.label}{item.available === false ? " · unavailable in this market" : item.venue_verified === false ? " · venue confirmation required" : ""}</MenuItem>)}
        </TextField>
        {[...new Set(types.filter((item) => item.available === false).map((item) => item.disabled_reason).filter(Boolean))].map((reason) => <Typography key={reason} variant="caption" color="text.secondary">{reason}</Typography>)}
        {type?.warning && <Alert severity="info">{type.warning}</Alert>}
        {draft.bid_type === "linked_block" && <Alert severity="info">The child depends on its parent. The parent can be accepted without the child; this does not guarantee that both legs of a battery cycle clear.</Alert>}
        {draft.bid_type === "exclusive_block" && <Alert severity="info">The group’s accepted ratios must total at most 1. With partial acceptance, more than one alternative can be partly accepted.</Alert>}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField select label="Direction" value={draft.side || ""} onChange={(event) => update("side", event.target.value)} fullWidth>
            {directions.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{labelOf(item)}</MenuItem>)}
          </TextField>
          <TextField label="Quantity (MW)" type="number" value={draft.quantity_mw ?? ""} onChange={(event) => update("quantity_mw", event.target.value)} fullWidth inputProps={{ step: "any" }} />
          <TextField label={`Price (${book.market?.price_unit || "EUR/MWh"})`} type="number" value={draft.price ?? ""} onChange={(event) => update("price", event.target.value)} fullWidth inputProps={{ step: "any" }} />
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {["period_start", "period_end"].map((field) => <TextField key={field} select label={field === "period_start" ? "First delivery period" : "Last delivery period (included)"} value={draft[field] || ""} onChange={(event) => update(field, event.target.value)} fullWidth>
            {periods.map((period) => <MenuItem key={period.index} value={period.index} disabled={period.available === false}>{periodLabel(period)}</MenuItem>)}
          </TextField>)}
        </Stack>
        {fields.filter((field) => !COMMON.has(field) && !ARRAY_FIELDS.has(field) && (field !== "expires_at" || draft.time_in_force === "NON")).map((field) => {
          const choices = field === "parent_id" ? orders.filter((item) => item.id !== draft.id && ["block", "profile_block", "linked_block"].includes(item.bid_type)).map((item) => ({ id: item.id, label: `${item.id} · ${item.side} · periods ${item.period_start}–${item.period_end}` }))
            : field === "time_in_force" ? conditions
            : field === "product" ? (book.market?.products || ["fcr", "afrr", "mfrr"]) : null;
          return <TextField key={field} label={LABELS[field] || field.replaceAll("_", " ")} value={draft[field] ?? ""}
            onChange={(event) => update(field, event.target.value)} select={Boolean(choices)} required={required.has(field)}
            type={NUMERIC.has(field) ? "number" : "text"} inputProps={{ step: "any" }} fullWidth>
            {choices?.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{labelOf(item)}</MenuItem>)}
          </TextField>;
        })}
        {fields.includes("profile_mw") && <TextField label="Profile quantities (MW)" value={draft.profile_mw || ""} onChange={(event) => update("profile_mw", event.target.value)} multiline minRows={2}
          helperText="One quantity for each selected period, separated by commas. The order quantity must equal the highest profile value." required={required.has("profile_mw")} />}
        {fields.includes("curve_steps") && <Box>
          <Typography variant="subtitle2">Price steps · incremental quantities</Typography>
          <Typography variant="caption">Enter the additional MW offered at each price. Their sum must equal the order quantity; the order price must match the first step.</Typography>
          {(draft.curve_steps || []).map((step, index) => <Stack direction="row" spacing={1} key={index} sx={{ mt: 1 }}>
            {["quantity_mw", "price"].map((field) => <TextField key={field} size="small" type="number" inputProps={{ step: "any" }} label={field === "price" ? `Step price (${book.market?.price_unit || "EUR/MWh"})` : "Additional MW"} value={step[field]} onChange={(event) => update("curve_steps", draft.curve_steps.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: event.target.value } : row))} />)}
            <IconButton aria-label={`Remove step ${index + 1}`} onClick={() => update("curve_steps", draft.curve_steps.filter((_, rowIndex) => rowIndex !== index))}><DeleteOutlineIcon /></IconButton>
          </Stack>)}
          <Button startIcon={<AddIcon />} onClick={() => update("curve_steps", [...(draft.curve_steps || []), { quantity_mw: "", price: "" }])}>Add price step</Button>
        </Box>}
        <Typography variant="caption" color="text.secondary">Order ID: {draft.id}. Prices, quantities and conditions are checked by the backend when the draft is saved.</Typography>
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={finish}>Keep order</Button></DialogActions>
  </Dialog>;
}

const baselineFor = (book) => ({
  orders: book.orders || [],
  expected_revision: book.revision,
  expected_configuration_version_id: book.current_configuration_version_id,
  expected_rules_version: book.current_rules_version,
});

function BookEditor({ userId, projectId, assetId, deliveryDay, tradingMarket, accessToken, onCatalog, onDirty, writeDisabled, exportDisabled }) {
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  const [book, setBook] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [recovered, setRecovered] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(undefined);
  const [reload, setReload] = useState(0);
  const scope = useMemo(() => ({ asset_id: assetId, delivery_day: deliveryDay, trading_market: tradingMarket }), [assetId, deliveryDay, tradingMarket]);
  const cacheKey = bidDraftKey(userId, projectId, scope);
  const staleBaseline = Boolean(book && baseline) && (
    baseline.expected_revision !== book.revision ||
    baseline.expected_configuration_version_id !== book.current_configuration_version_id ||
    baseline.expected_rules_version !== book.current_rules_version
  );
  const dirty = Boolean(book && baseline) && (staleBaseline || JSON.stringify(orders) !== JSON.stringify(baseline.orders));
  const needsReview = Boolean(book?.configuration_changed || book?.rules_changed);
  useEffect(() => { onDirty(dirty); return () => onDirty(false); }, [dirty, onDirty]);
  useEffect(() => {
    if (loading || !book || !baseline) return;
    if (dirty) rememberBidDraft(cacheKey, { userId, projectId, scope, orders, baseline });
    else forgetBidDraft(cacheKey);
  }, [loading, book, baseline, dirty, cacheKey, userId, projectId, scope, orders]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(""); setBook(null); setBaseline(null); setOrders([]); setRecovered(false);
    loadMarketBidBook(projectId, scope, accessTokenRef.current, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      // Access must succeed before any cached order is made visible. The original
      // expectations stay attached to recovered edits even if the server changed.
      const cached = readBidDraft(cacheKey);
      setBook(result); setBaseline(cached?.baseline || baselineFor(result));
      setOrders(cached?.orders || result.orders || []); setRecovered(Boolean(cached));
      onCatalog(result.markets || []);
    }).catch((failure) => { if (!controller.signal.aborted) setError(failure.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [projectId, scope, reload, onCatalog, cacheKey]);
  const save = async () => {
    const savingDraft = { orders, baseline };
    setSaving(true); setError(""); setNotice("");
    try {
      const result = await saveMarketBidBook(projectId, scope, {
        expected_revision: baseline.expected_revision,
        expected_configuration_version_id: baseline.expected_configuration_version_id,
        expected_rules_version: baseline.expected_rules_version,
        orders,
      }, accessToken);
      forgetBidDraft(cacheKey, savingDraft);
      setBook(result); setBaseline(baselineFor(result)); setOrders(result.orders || []); setRecovered(false);
      setNotice("Draft saved. No orders have been sent to a market.");
    } catch (failure) { setError(failure.message); }
    finally { setSaving(false); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([serializeBidBook({ ...book, project_id: projectId, ...scope })], { type: "application/json" }));
    const anchor = document.createElement("a"); anchor.href = url;
    anchor.download = `bid-book-${tradingMarket}-${deliveryDay}.json`; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  if (loading) return <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography>Loading bid book…</Typography></Stack>;
  if (!book) return <Alert severity="warning" action={<Button onClick={() => setReload((value) => value + 1)}>Retry</Button>}>{error || "The bid book is unavailable."}</Alert>;
  return <Stack spacing={2}>
    <Stack direction="row" flexWrap="wrap" gap={1}>
      <Chip label={`Zone: ${book.bidding_zone}`} variant="outlined" />
      <Chip label={`Delivery timezone: ${book.market_timezone || book.timezone}`} variant="outlined" />
      <Chip label={`Period times: ${book.timezone}`} variant="outlined" />
      <Chip label={`${book.periods?.length || 0} delivery periods`} variant="outlined" />
      <Chip label={dirty ? "Unsaved draft" : `Saved revision ${book.revision}`} color={dirty ? "warning" : "default"} />
    </Stack>
    <Alert severity="info">Prepare and save orders for this market. Exchange submission requires a connected broker or venue and verified asset eligibility. No orders are sent from this editor.</Alert>
    {book.market?.warning && <Alert severity="info">{book.market.warning}</Alert>}
    {recovered && <Alert severity="info">Recovered unsaved orders from this browser session.</Alert>}
    {staleBaseline && <Alert severity="warning">The saved draft or its settings changed while you were away. Your edits are retained with their original version checks. Discard edits to review the latest saved draft.</Alert>}
    {needsReview && <Alert severity="warning">The asset configuration or bidding rules changed after this draft was saved. Review the orders, then validate and save them again.</Alert>}
    {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
    {notice && <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert>}
    <Stack direction="row" flexWrap="wrap" gap={1}>
      <Button startIcon={<AddIcon />} variant="outlined" disabled={writeDisabled || saving} onClick={() => setEditing(null)}>Add order</Button>
      <Button variant="contained" disabled={(!dirty && !needsReview) || writeDisabled || saving} onClick={save}>{saving ? "Saving…" : needsReview ? "Validate and save draft" : "Save draft"}</Button>
      <Button disabled={!dirty || saving} onClick={() => { forgetBidDraft(cacheKey); setBaseline(baselineFor(book)); setOrders(book.orders || []); setRecovered(false); setError(""); }}>Discard edits</Button>
      <Button disabled={dirty || saving} onClick={() => setReload((value) => value + 1)}>Reload saved draft</Button>
      <Button disabled={dirty || needsReview || !book.orders?.length || exportDisabled || saving} onClick={download}>Export review file</Button>
    </Stack>
    {dirty && <Typography variant="caption">Save or discard changes before switching the asset, market or delivery day. Unsaved orders are kept when navigating within the app, but only for this browser session.</Typography>}
    {!orders.length ? <Typography color="text.secondary">No orders for this asset, market and delivery day. Add an order to start a draft.</Typography> : <TableContainer>
      <Table size="small" aria-label="Market bid orders"><TableHead><TableRow>
        {["Order", "Type", "Direction", "Delivery periods", "MW", `Price (${book.market?.price_unit || "EUR/MWh"})`, "Conditions", "Actions"].map((label) => <TableCell key={label}>{label}</TableCell>)}
      </TableRow></TableHead><TableBody>{orders.map((order, index) => <TableRow key={order.id}>
        <TableCell title={order.id}>{index + 1}</TableCell>
        <TableCell>{book.market?.bid_types?.find((type) => type.id === order.bid_type)?.label || order.bid_type}</TableCell>
        <TableCell>{order.side}</TableCell><TableCell>{order.period_start}–{order.period_end}</TableCell>
        <TableCell>{order.quantity_mw}</TableCell><TableCell>{order.price}</TableCell>
        <TableCell sx={{ maxWidth: 260, overflowWrap: "anywhere" }}>{[
          order.time_in_force, order.parent_id && `Parent: ${order.parent_id}`, order.exclusive_group && `Group: ${order.exclusive_group}`,
          order.link_group && `Basket: ${order.link_group}`, order.min_acceptance_ratio != null && `MAR: ${order.min_acceptance_ratio}`,
          order.curve_steps?.length && `${order.curve_steps.length} steps`, order.profile_mw?.length && "Variable profile",
          order.display_quantity_mw && `Visible: ${order.display_quantity_mw} MW`, order.duration_periods && `${order.duration_periods} periods`,
        ].filter(Boolean).join(" · ") || "—"}</TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}><IconButton size="small" aria-label={`Edit order ${index + 1}`} disabled={writeDisabled || saving} onClick={() => setEditing(order)}><EditOutlinedIcon fontSize="small" /></IconButton>
          <IconButton size="small" aria-label={`Delete order ${index + 1}`} disabled={writeDisabled || saving} onClick={() => setOrders((previous) => previous.filter((item) => item.id !== order.id))}><DeleteOutlineIcon fontSize="small" /></IconButton></TableCell>
      </TableRow>)}</TableBody></Table></TableContainer>}
    <OrderEditor open={editing !== undefined} order={editing} book={book} orders={orders} onClose={() => setEditing(undefined)} onSave={(order) => {
      setOrders((previous) => previous.some((item) => item.id === order.id) ? previous.map((item) => item.id === order.id ? order : item) : [...previous, order]);
      setEditing(undefined); setNotice("");
    }} />
  </Stack>;
}

function ProjectBidBook({ userId, currentProject, activeAsset, assets, deliveryDay: initialDay, accessToken, writeDisabled = false, exportDisabled = false }) {
  const [recoveryScope] = useState(() => lastBidDraftScope(userId, currentProject.id));
  const [assetId, setAssetId] = useState(recoveryScope?.asset_id || activeAsset?.id || "");
  const [tradingMarket, setTradingMarket] = useState(recoveryScope?.trading_market || "DAM");
  const [catalog, setCatalog] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [deliveryDay, onDeliveryDayChange] = useState(recoveryScope?.delivery_day || initialDay || "");
  useEffect(() => {
    if (!dirty && !assets.some((asset) => asset.id === assetId)) setAssetId(activeAsset?.id || "");
  }, [activeAsset?.id, assetId, assets, dirty]);
  const selectedAsset = assets.find((asset) => asset.id === assetId);
  return <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
    <Stack spacing={2.5}>
      <Box><Typography variant="h6" fontWeight={700}>Market orders</Typography><Typography variant="body2" color="text.secondary">One delivery day, with a separate bid book for each asset and market.</Typography></Box>
      <Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems="flex-start" flexWrap="wrap">
        <AppDatePicker label="Delivery day" value={deliveryDay} onChange={onDeliveryDayChange} disabled={dirty} sx={{ width: 220, minWidth: 220, flexShrink: 0 }} />
        <TextField select label="Asset" value={assetId} onChange={(event) => setAssetId(event.target.value)} disabled={dirty} size="small" sx={{ minWidth: 230 }}>
          {assets.map((asset) => <MenuItem key={asset.id} value={asset.id}>{asset.name}</MenuItem>)}
        </TextField>
        <TextField select label="Trading market" value={tradingMarket} onChange={(event) => setTradingMarket(event.target.value)} disabled={dirty} size="small" sx={{ minWidth: 260 }}>
          {(catalog.length ? catalog : [{ id: tradingMarket, label: tradingMarket === "DAM" ? "Day-ahead (DAM)" : tradingMarket }]).map((market) => <MenuItem key={market.id} value={market.id}>{market.label}</MenuItem>)}
        </TextField>
      </Stack>
      {selectedAsset && deliveryDay ? <BookEditor key={`${currentProject.id}:${assetId}:${deliveryDay}:${tradingMarket}`} userId={userId} projectId={currentProject.id} assetId={assetId} deliveryDay={deliveryDay} tradingMarket={tradingMarket} accessToken={accessToken} onCatalog={setCatalog} onDirty={setDirty} writeDisabled={writeDisabled} exportDisabled={exportDisabled} /> : <Alert severity="info">Choose a saved asset and delivery day.</Alert>}
    </Stack>
  </Paper>;
}

export default function MarketBidBook(props) {
  const { currentProject, activeAsset, assets = [] } = useFile();
  const { user } = useAuth();
  if (!user?.id) return <Alert severity="info">Sign in to prepare market orders.</Alert>;
  if (!currentProject?.id) return <Alert severity="info">Save the project before preparing market orders.</Alert>;
  return <ProjectBidBook key={`${user.id}:${currentProject.id}`} {...props} userId={user.id}
    currentProject={currentProject} activeAsset={activeAsset} assets={assets} />;
}
